import { txt } from '../utils/dom.js';
import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { getIcon } from '../utils/icons-library.js';


export const processBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    // `safe()` (utils/contrast.js) YA dejó `p.items` en un array, así que este `|| []` es
    // cinturón, NO un default de contenido: con 0 items este bloque se pinta VACÍO (titular +
    // contenedor sin nada, y sin ningún "+" con el que añadir desde el lienzo). De que no
    // llegue a publicarse así se encarga la guarda de CPU `_fix_bloques_sin_contenido`
    // (llm_sanitizers.py::_ARRAY_DE_CONTENIDO), que descarta el bloque al generar.
    const items = p.items || [];
    const variant = p.variant || 'v1';

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // --- Toolbar por item ---
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="items" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="items" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn icon-picker-btn" data-array="items" data-index="${i}" title="Cambiar icono" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.palette || '🎨'}</button>
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="items" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // --- Número / Icono ---
    const stepIcon = (item, i, size = 24) => {
        return item.icon ? getIcon(item.icon, size) : `0${i + 1}`;
    };

    // --- Encabezado compartido ---
    const header = `
        <div style="text-align:center; margin-bottom:60px;">
            ${p.title ? `<h2 data-field="title" contenteditable="true" style="font-size:calc(clamp(2rem,4cqw,3rem) * var(--type-scale, 1)); font-weight:900; margin-bottom:16px;">${p.title}</h2>` : ''}
            ${p.subtitle ? `<p data-field="subtitle" contenteditable="true" style="opacity:0.6; font-size:calc(1.1rem * var(--text-scale, 1));">${p.subtitle}</p>` : ''}
        </div>`;

    // ═══════════════════════════════════════════
    // v1 — Horizontal Steps con línea conectora
    // ═══════════════════════════════════════════
    if (variant === 'v1') {
        return `
    <section id="bloque-process-${index}" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
        ${header}
        <div class="process-steps-h">
            ${items.map((item, i) => `
            <div class="process-step sortable-item stagger-item" data-item-index="${i}" data-array-name="items" style="--stagger-i:${i};">
                ${itemToolbar(i)}
                <div class="step-number">${stepIcon(item, i)}</div>
                ${i < items.length - 1 ? '<div class="step-connector"></div>' : ''}
                <h3 data-field="name" contenteditable="true" style="font-size:calc(1.1rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight:700; margin:16px 0 8px;">${txt(item.name)}</h3>
                <p data-field="desc" contenteditable="true" style="opacity:0.65; font-size:calc(0.9rem * var(--text-scale, 1));">${txt(item.desc)}</p>
            </div>`).join('')}
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v2 — Zigzag con conectores SVG animados
    // ═══════════════════════════════════════════
    if (variant === 'v2') {
        const svgLeft = `<svg width="80" height="120" viewBox="0 0 80 120" fill="none">
            <g class="pz-lines-group">
                <path class="pz-line-main" d="M10 10 Q40 10 40 60 Q40 110 70 110" stroke="currentColor" stroke-width="1.5" fill="none"/>
                <path class="pz-line-sub" d="M10 10 Q40 10 40 60 Q40 110 70 110" stroke="currentColor" stroke-width="1" fill="none" transform="translate(4,0)" opacity="0.3"/>
            </g>
            <circle class="pz-dot-outer" cx="70" cy="110" r="5" fill="none" stroke="currentColor" stroke-width="1.5"/>
            <circle class="pz-dot-inner" cx="70" cy="110" r="2" fill="currentColor" opacity="0.4"/>
        </svg>`;

        const svgRight = `<svg width="80" height="120" viewBox="0 0 80 120" fill="none">
            <g class="pz-lines-group">
                <path class="pz-line-main" d="M70 10 Q40 10 40 60 Q40 110 10 110" stroke="currentColor" stroke-width="1.5" fill="none"/>
                <path class="pz-line-sub" d="M70 10 Q40 10 40 60 Q40 110 10 110" stroke="currentColor" stroke-width="1" fill="none" transform="translate(-4,0)" opacity="0.3"/>
            </g>
            <circle class="pz-dot-outer" cx="10" cy="110" r="5" fill="none" stroke="currentColor" stroke-width="1.5"/>
            <circle class="pz-dot-inner" cx="10" cy="110" r="2" fill="currentColor" opacity="0.4"/>
        </svg>`;

        return `
    <section id="bloque-process-${index}" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
        ${header}
        <div class="process-zigzag" style="max-width:900px; margin:0 auto;">
            ${items.map((item, i) => {
                const isLeft = i % 2 === 0;
                const svg = isLeft ? svgLeft : svgRight;
                const showConnector = i < items.length - 1;
                const stepNum = String(i + 1).padStart(2, '0');
                return `
            <div class="pz-item ${isLeft ? 'left' : 'right'} sortable-item" data-item-index="${i}" data-array-name="items">
                ${isLeft ? `
                <div class="pz-card">
                    ${itemToolbar(i)}
                    <div class="pz-badge">${stepNum}</div>
                    ${item.icon ? `<div class="pz-icon">${getIcon(item.icon, 22)}</div>` : ''}
                    <h3 data-field="name" contenteditable="true">${txt(item.name)}</h3>
                    <p data-field="desc" contenteditable="true">${txt(item.desc)}</p>
                </div>
                ${showConnector ? `<div class="pz-svg-wrap">${svg}</div>` : '<div class="pz-empty"></div>'}
                <div class="pz-empty"></div>
                ` : `
                <div class="pz-empty"></div>
                ${showConnector ? `<div class="pz-svg-wrap">${svg}</div>` : '<div class="pz-empty"></div>'}
                <div class="pz-card">
                    ${itemToolbar(i)}
                    <div class="pz-badge">${stepNum}</div>
                    ${item.icon ? `<div class="pz-icon">${getIcon(item.icon, 22)}</div>` : ''}
                    <h3 data-field="name" contenteditable="true">${txt(item.name)}</h3>
                    <p data-field="desc" contenteditable="true">${txt(item.desc)}</p>
                </div>
                `}
            </div>`;
            }).join('')}
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v3 — Cards numeradas + barra de progreso
    // ═══════════════════════════════════════════
    return `
    <section id="bloque-process-${index}" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
        ${header}
        <div class="process-progress"></div>
        <div class="process-cards">
            ${items.map((item, i) => `
            <div class="card process-step sortable-item stagger-item" data-item-index="${i}" data-array-name="items" style="--stagger-i:${i}; position:relative; padding:40px 30px; overflow:visible;">
                ${itemToolbar(i)}
                <span class="ghost-number" aria-hidden="true" style="font-size: 12rem; top: 0px; right: -20px; transform: translateY(-10%); opacity: 0.1;">${String(i + 1).padStart(2, '0')}</span>
                <div style="position:relative; z-index:1;">
                    <div style="margin-bottom:16px;">${item.icon ? getIcon(item.icon, 28) : ''}</div>
                    <h3 data-field="name" contenteditable="true" style="font-size:calc(1.15rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight:700; margin-bottom:10px;">${txt(item.name)}</h3>
                    <p data-field="desc" contenteditable="true" style="opacity:0.65; font-size:calc(0.9rem * var(--text-scale, 1));">${txt(item.desc)}</p>
                </div>
            </div>`).join('')}
        </div>
    </section>`;
};

// ═══════════════════════════════════════════
// Animación v2: IntersectionObserver nativo
